import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Reply, CheckCircle2, X, Send, Loader2 } from "lucide-react";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

// Feb 2026 — CRM Manager / Executive replies to client-raised concerns.
// Every reply is stored on the concern doc AND surfaces on the client
// dashboard via db.client_alerts. CRM can also mark the concern resolved.

export default function CrmConcernsPage() {
  const [concerns, setConcerns] = useState([]);
  const [replying, setReplying] = useState(null); // concern object

  const load = async () => {
    try {
      const { data } = await api.get("/crm/concerns");
      setConcerns(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => concerns.filter((c) => projectMatches(c, q)),
    [concerns, q]
  );
  const open = filtered.filter(c => c.status !== "resolved");
  const resolved = filtered.filter(c => c.status === "resolved");

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM · Alerts</p>
        <h1 className="font-display text-3xl text-white mt-1">Client Concerns</h1>
        <p className="text-xs text-gray-500 font-mono mt-1">Reply directly to raised concerns. Your response appears on the client dashboard instantly.</p>
      </div>

      <div className="px-10 py-8 space-y-8">
        <ProjectSearchBar
          value={q}
          onChange={setQ}
          placeholder="Search concerns by client, project, or city…"
          resultCount={filtered.length}
          totalCount={concerns.length}
          testid="crm-concerns-search"
        />
        {concerns.length > 0 && filtered.length === 0 && q.trim() ? (
          <ProjectSearchEmpty query={q} testid="crm-concerns-search-empty" />
        ) : (
          <>
            <ConcernList title="Open" testid="open-concerns" concerns={open}
                         onReply={setReplying}
                         empty="No open concerns · all clear." />
            <ConcernList title="Resolved" testid="resolved-concerns" concerns={resolved}
                         onReply={setReplying}
                         empty="No resolved concerns yet." mutedHeader />
          </>
        )}
      </div>

      {replying && (
        <ReplyDialog
          concern={replying}
          onClose={() => setReplying(null)}
          onDone={() => { setReplying(null); load(); }}
        />
      )}
    </div>
  );
}

function ConcernList({ title, testid, concerns, onReply, empty, mutedHeader }) {
  return (
    <section data-testid={testid}>
      <p className={`text-[10px] font-mono uppercase tracking-[0.3em] mb-3 ${mutedHeader ? "text-gray-500" : "text-[#d4af37]"}`}>
        {title} · {concerns.length}
      </p>
      {concerns.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">{empty}</p>
      ) : (
        <div className="space-y-3">
          {concerns.map(c => (
            <ConcernCard key={c.id} concern={c} onReply={onReply} />
          ))}
        </div>
      )}
    </section>
  );
}

function ConcernCard({ concern, onReply }) {
  const isResolved = concern.status === "resolved";
  return (
    <div className={`iz-card p-5 ${isResolved ? "opacity-70 border-emerald-500/20" : "border-[#d4af37]/30"}`}
         data-testid={`concern-${concern.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border ${isResolved ? "bg-emerald-500/5 border-emerald-500/30" : "bg-[#d4af37]/10 border-[#d4af37]/30"}`}>
          {isResolved ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <AlertTriangle className="w-4 h-4 text-[#d4af37]"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
            <p className="text-white font-medium">{concern.subject}</p>
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              {new Date(concern.created_at).toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-400 whitespace-pre-wrap">{concern.description}</p>
          <div className="mt-2 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-gray-500 flex-wrap">
            <span>By: {concern.raised_by_email}</span>
            {concern.tag_all && <span className="text-[#d4af37]">· Tag-All</span>}
            <span className={`px-2 py-0.5 rounded border ${isResolved ? "border-emerald-500/40 text-emerald-400" : "border-[#d4af37]/40 text-[#d4af37]"}`}>
              {concern.status}
            </span>
          </div>

          {(concern.replies || []).length > 0 && (
            <div className="mt-4 pl-3 border-l-2 border-[#d4af37]/40 space-y-2" data-testid={`replies-thread-${concern.id}`}>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37]">
                Reply Thread · {concern.replies.length}
              </p>
              {concern.replies.map(r => (
                <div key={r.id} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-md p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-mono text-gray-300">
                      {r.by_user_name} <span className="text-gray-600">· {r.by_role}</span>
                    </p>
                    <p className="text-[10px] font-mono text-gray-600">{new Date(r.at).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-white whitespace-pre-wrap">{r.message}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <button onClick={() => onReply(concern)}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid={`reply-btn-${concern.id}`}>
              <Reply className="w-3 h-3"/> {isResolved ? "Add note" : (concern.replies?.length ? "Reply again" : "Reply to client")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReplyDialog({ concern, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [markResolved, setMarkResolved] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length === 0) { toast.error("Reply cannot be empty"); return; }
    setSaving(true);
    try {
      await api.post(`/crm/concerns/${concern.id}/reply`, {
        message: message.trim(),
        mark_resolved: markResolved,
      });
      toast.success(markResolved ? "Replied & marked resolved · Client notified" : "Reply sent · Client notified");
      onDone?.();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="concern-reply-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM Reply</p>
            <h3 className="font-display text-lg text-white mt-1">{concern.subject}</h3>
            <p className="text-[11px] font-mono text-gray-500 mt-0.5">To: {concern.raised_by_email}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="reply-close">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded p-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Original concern</p>
            <p className="text-xs text-gray-300 whitespace-pre-wrap">{concern.description}</p>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Your reply *</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
                       placeholder="Explain what's happening, next steps, timeline..."
                       className="iz-input" data-testid="reply-message"/>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <input type="checkbox" checked={markResolved}
                    onChange={(e) => setMarkResolved(e.target.checked)}
                    className="accent-[#d4af37]" data-testid="reply-mark-resolved"/>
            Mark this concern as resolved after sending
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="iz-btn-secondary" data-testid="reply-cancel">Cancel</button>
          <button type="submit" disabled={saving}
                   className="iz-btn-primary flex items-center gap-2"
                   data-testid="reply-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            {markResolved ? "Send & resolve" : "Send reply"}
          </button>
        </div>
      </form>
    </div>
  );
}
